import { defineMessages } from 'gatsby-plugin-react-intl';

import {
  formatNumberRange, formatSelectKey, getFullName, getInstructorPositionOrg, removeHtmlTags,
} from './formatting';
import { getDuration, getTruncatedDescription } from './course';
import { sortAlphabeticallyByProp } from './sorting';

const messages = defineMessages({
  'prospectus.program_pathway.effort_range': {
    id: 'prospectus.program_pathway.effort_range',
    description: 'Estimated range of effort to complete this program',
    defaultMessage: '{effortRange} hours of effort',
  },
  'prospectus.program.duration.total': {
    id: 'prospectus.program.duration.total',
    description: 'Estimated total time to complete different types of programs',
    defaultMessage: '{typeKey, select, '
      + 'micromasters {1 year total} '
      + 'professional_certificate {2-4 months total} '
      + 'xseries {4-6 months total}'
      + 'other {}}',
  },
  'prospectus.program.main.statistic.duration.months': {
    id: 'prospectus.program.main.statistic.duration.months',
    description: 'Describe estimated duration of the program in months',
    defaultMessage: '{totalMonths, plural, '
      + 'one {# month} '
      + 'other {# months}}',
  },
  'prospectus.program.main.statistic.duration.years': {
    id: 'prospectus.program.main.statistic.duration.years',
    description: 'Describe estimated duration of the program in years',
    defaultMessage: '{totalYears, plural, '
      + 'one {# year} '
      + 'other {# years}}',
  },
  'prospectus.program.main.statistic.duration.yearAndMonth': {
    id: 'prospectus.program.main.statistic.duration.yearAndMonth',
    description: 'Describe estimated duration of the program in year and month',
    defaultMessage: '1 year 1 month',
  },
  'prospectus.program.main.statistic.duration.yearAndMonths': {
    id: 'prospectus.program.main.statistic.duration.yearAndMonths',
    description: 'Describe estimated duration of the program in year and months',
    defaultMessage: '1 year {monthCount} months',
  },
  'prospectus.program.main.statistic.duration.yearsAndMonth': {
    id: 'prospectus.program.main.statistic.duration.yearsAndMonth',
    description: 'Describe estimated duration of the program in years and month',
    defaultMessage: '{yearCount} years 1 month',
  },
  'prospectus.program.main.statistic.duration.yearsAndMonths': {
    id: 'prospectus.program.main.statistic.duration.yearsAndMonths',
    description: 'Describe estimated duration of the program in years and months',
    defaultMessage: '{yearCount} years {monthCount} months',
  },
});

const programIsMasters = type => type.toLowerCase() === 'masters';

const programIsMicroMasters = type => type.toLowerCase() === 'micromasters';

const programIsProfessionalCertificate = type => type.toLowerCase() === 'professional certificate';

const getDefaultActiveProgram = (programs = []) => {
  if (programs.length < 2) {
    return programs[0];
  }

  const microMasters = programs.find(({ type }) => programIsMicroMasters(type));
  if (microMasters) {
    return microMasters;
  }

  const professionalCertificate = programs.find(({ type }) => programIsProfessionalCertificate(type));

  if (professionalCertificate) {
    return professionalCertificate;
  }

  return programs[0];
};

const getProgramEffortRange = (courseRun, intl) => {
  const {
    maxEffort,
    minEffort,
    weeksToComplete: totalWeeks,
  } = courseRun;
  if (minEffort || maxEffort) {
    return intl.formatMessage(
      messages['prospectus.program_pathway.effort_range'],
      {
        effortRange: formatNumberRange(
          minEffort * totalWeeks,
          maxEffort * totalWeeks,
          intl,
        ),
      },
    );
  }
  return '';
};

const getProgramTypeDuration = (type, intl) => intl.formatMessage(
  messages['prospectus.program.duration.total'],
  { typeKey: formatSelectKey(type) },
);

const sortProgramsByPreferredType = (programs = []) => {
  if (programs.length < 2) {
    return programs;
  }

  return sortAlphabeticallyByProp(programs, 'type');
};

const getWebviewUrl = (url) => {
  const startsWithFragment = (piece, pathFragment, matchOnlySubpages) => {
    const prefix = `${pathFragment}/`;
    if (piece === pathFragment || piece === prefix) {
      return !matchOnlySubpages;
    }

    return piece.startsWith(prefix);
  };

  // url may be full or relative, if relative take GATSBY_SITE_DOMAIN as base
  const urlObj = url.startsWith('http') ? new URL(url) : new URL(url, process.env.GATSBY_SITE_DOMAIN);
  const urlPath = urlObj.pathname.slice(1); // URL.pathname always includes initial slash
  let urlSearch = urlObj.search;
  if (urlSearch.length > 0) {
    // URL.search string includes initial ?, replace with & to concatenate to webview url
    urlSearch = `${urlSearch.slice(1)}`;
  }
  if (startsWithFragment(urlPath, 'course')
    || startsWithFragment(urlPath, 'learn')
    || startsWithFragment(urlPath, 'executive-education')
    || startsWithFragment(urlPath, 'boot-camps')
  ) {
    return `edxapp://course_info?path_id=${urlPath}${urlSearch && `&${urlSearch}`}`;
  }
  if (startsWithFragment(urlPath, 'program', true)
    || startsWithFragment(urlPath, 'bachelors', true)
    || startsWithFragment(urlPath, 'masters', true)
    || startsWithFragment(urlPath, 'micromasters', true)
    || startsWithFragment(urlPath, 'microbachelors', true)
    || startsWithFragment(urlPath, 'gfa', true)
    || startsWithFragment(urlPath, 'professional-education', true)
    || startsWithFragment(urlPath, 'xseries', true)
    || startsWithFragment(urlPath, 'professional-certificate', true)
    || startsWithFragment(urlPath, 'doctorate', true)
    || startsWithFragment(urlPath, 'license', true)
    || startsWithFragment(urlPath, 'certificates', true)
  ) {
    return `edxapp://program_info?path_id=${urlPath}${urlSearch && `&${urlSearch}`}`;
  }
  if (startsWithFragment(urlPath, 'search')) {
    return `/webview/v2/${urlPath}${urlSearch && `?${urlSearch}`}&device=app&webview=true`;
  }
  // if its not a deep link, just fallback to the regular website
  return `/${urlPath}${urlSearch && `?${urlSearch}`}`;
};

const getHighestPriceProgram = (programs) => programs.reduce((maxPriceProgram, program) => (
  parseFloat(maxPriceProgram.program.programOriginalPrice) > parseFloat(program.program.programOriginalPrice)
    ? maxPriceProgram
    : program));

const getProgramPacing = (courses) => {
  const coursePacings = courses.map((course) => {
    if (course?.activeCourseRun) {
      return course.activeCourseRun.pacingType;
    }
    return false;
  });

  // Set removes duplicates
  const uniquePacings = new Set(coursePacings);

  if (uniquePacings.size === 1) {
    return coursePacings[0];
  }

  return 'mixed';
};

const getMinHoursOfEffort = (courses) => {
  const reducer = (totalHours, course) => {
    let additionalHours = 0;
    if (course?.activeCourseRun) {
      additionalHours = Number.parseInt(
        course.activeCourseRun.minEffort * course.activeCourseRun.weeksToComplete,
        10,
      );
    }

    return totalHours + additionalHours;
  };

  return Number.parseInt(courses.reduce(reducer, 0), 10);
};

const getMaxHoursOfEffort = (courses) => {
  const reducer = (totalHours, course) => {
    let additionalHours = 0;
    if (course?.activeCourseRun) {
      additionalHours = Number.parseInt(
        course.activeCourseRun.maxEffort * course.activeCourseRun.weeksToComplete,
        10,
      );
    }

    return totalHours + additionalHours;
  };

  return Number.parseInt(courses.reduce(reducer, 0), 10);
};

const getTotalWeeks = (courses) => {
  const reducer = (totalWeeks, course) => {
    let additionalWeeks = 0;
    if (course?.activeCourseRun) {
      additionalWeeks = Number.parseInt(course.activeCourseRun.weeksToComplete, 10);
    }

    return totalWeeks + additionalWeeks;
  };

  return Number.parseInt(courses.reduce(reducer, 0), 10);
};

const getTotalEstimatedEffortInHoursPerWeek = (courses) => {
  const minTotalHours = getMinHoursOfEffort(courses);
  const maxTotalHours = getMaxHoursOfEffort(courses);
  const totalWeeks = getTotalWeeks(courses);

  const minAverageHours = Math.round(minTotalHours / totalWeeks);

  if (Number.isNaN(minTotalHours) || Number.isNaN(maxTotalHours) || Number.isNaN(totalWeeks)
    || Number.isNaN(minAverageHours)) {
    // returning null instead of NaN allows us to hide the section with this information instead of just displaying
    // 'NaN's everywhere
    return null;
  }

  if (minTotalHours === maxTotalHours) {
    return minAverageHours;
  }

  const maxAverageHours = Math.round(maxTotalHours / totalWeeks);
  return `${minAverageHours} - ${maxAverageHours}`;
};

const getProgramDuration = (courses, intl) => {
  const totalWeeks = getTotalWeeks(courses);
  const totalMonths = Math.round(totalWeeks / 4);

  if (totalMonths < 12) {
    return intl.formatMessage(
      messages['prospectus.program.main.statistic.duration.months'],
      {
        totalMonths,
      },
    );
  }

  const totalYears = Math.floor(totalMonths / 12);
  const totalRemainderMonths = Math.round(totalMonths % 12);

  if (totalRemainderMonths === 0) {
    return intl.formatMessage(
      messages['prospectus.program.main.statistic.duration.years'],
      {
        totalYears,
      },
    );
  } if (totalYears === 1 && totalRemainderMonths === 1) {
    return intl.formatMessage(messages['prospectus.program.main.statistic.duration.yearAndMonth']);
  } if (totalYears === 1) {
    return intl.formatMessage(
      messages['prospectus.program.main.statistic.duration.yearAndMonths'],
      {
        monthCount: totalRemainderMonths,
      },
    );
  } if (totalRemainderMonths === 1) {
    return intl.formatMessage(
      messages['prospectus.program.main.statistic.duration.yearsAndMonth'],
      {
        yearCount: totalYears,
      },
    );
  }

  return intl.formatMessage(
    messages['prospectus.program.main.statistic.duration.yearsAndMonths'],
    {
      yearCount: totalYears,
      monthCount: totalRemainderMonths,
    },
  );
};

const getOffers = (programPrice) => [{
  '@type': 'Offer',
  category: 'Paid',
  priceCurrency: 'USD',
  price: programPrice,
  eligibleRegion: 'US',
}];

const getMappedInstructors = (staff) => {
  const instructorMap = staff.map(instructor => ({
    '@type': 'Person',
    name: getFullName(instructor),
    description: getInstructorPositionOrg(instructor.position),
    image: instructor.profileImageUrl,
  }));

  return instructorMap;
};

const getMappedCourseInstances = (courses) => {
  const mappedCourseInstances = courses.map(course => ({
    '@type': 'CourseInstance',
    instructor: getMappedInstructors(course?.activeCourseRun?.staff),
    name: course?.activeCourseRun?.title,
    courseMode: 'Online',
    courseSchedule: {
      '@type': 'Schedule',
      duration: getDuration(course?.activeCourseRun),
      repeatFrequency: 'Weekly',
      repeatCount: course?.activeCourseRun?.weeksToComplete,
    },
  }));

  return mappedCourseInstances;
};

const validProgramCourses = (courses) => {
  if (courses.length === 0) { return false; }

  for (let i = 0, len = courses.length; i < len; i++) {
    const course = courses[i];
    if (!course) {
      return false;
    }
    // adding extra validation for hasPart
    const courseRun = course?.activeCourseRun;
    if (!courseRun
      || !courseRun.staff
      || !courseRun.title
      || !courseRun.shortDescription
      || !courseRun.marketingUrl) { return false; }
  }

  return true;
};

const programValidFieldsForSchema = ({
  title, subtitle, overview, authoringOrganizations, programPrice, formattedCourses,
}) => {
  if (!title && !subtitle && !overview) { return false; }

  if (!programPrice) { return false; }

  if (authoringOrganizations.length === 0) { return false; }

  if (!validProgramCourses(formattedCourses)) { return false; }

  return true;
};

const getMappedCourseParts = (courses) => courses.map((course) => ({
  '@type': 'Course',
  name: course?.activeCourseRun?.title,
  url: course?.activeCourseRun?.marketingUrl,
  description: getTruncatedDescription(removeHtmlTags(course?.activeCourseRun?.shortDescription)),
}));

const getCourseSchemaOrgJsonLD = (program) => {
  const {
    title,
    subtitle,
    overview,
    authoringOrganizations,
    programPrice,
    courses,
  } = program;
  const formattedCourses = courses.map(({ course }) => course);

  if (!programValidFieldsForSchema({
    title, subtitle, overview, authoringOrganizations, programPrice, formattedCourses,
  })) {
    return null;
  }

  const json = {
    '@context': 'https://schema.org/',
    '@type': 'Course',
    name: title,
    description: getTruncatedDescription(removeHtmlTags(subtitle || overview || title || '')),
    provider: authoringOrganizations.map(organization => ({
      '@type': 'CollegeOrUniversity',
      name: organization.name,
      url: organization.marketingUrl,
    })),
    offers: getOffers(programPrice),
    hasCourseInstance: getMappedCourseInstances(formattedCourses),
    publisher: {
      '@type': 'Organization',
      name: 'edX',
      url: 'edx.org',
    },
    hasPart: getMappedCourseParts(formattedCourses),
  };

  return json;
};

export {
  getCourseSchemaOrgJsonLD,
  getDefaultActiveProgram,
  getHighestPriceProgram,
  getMaxHoursOfEffort,
  getMinHoursOfEffort,
  getProgramDuration,
  getProgramEffortRange,
  getProgramTypeDuration,
  getProgramPacing,
  getTotalEstimatedEffortInHoursPerWeek,
  getTotalWeeks,
  getWebviewUrl,
  sortProgramsByPreferredType,
  programIsProfessionalCertificate,
  programIsMasters,
  programIsMicroMasters,
  getMappedCourseParts,
};
